import React, { useEffect, useId, useRef, useState } from 'react'
import './DrawOutline.scss'

// "Drawn" outline: a stroke that traces the parent's border in the brand
// gradient, like a pen tracing the shape. Purely decorative (aria-hidden);
// the parent's stylesheet owns visibility, stroke width and the trace
// animation — see Chip.scss (chip__draw) for the canonical pattern.
// The parent element must be position: relative.
//
// The rect uses measured pixel geometry, not percentage/calc() attributes:
// when the trace animation promotes the svg to a compositing layer, Safari
// transiently rasterizes percentage geometry against the svg's intrinsic
// 300×150 size — the stroke shoots out well past the host before snapping
// to the right bounds. Explicit pixels sidestep that; until the first
// measurement lands there is no rect, so nothing can mis-draw.
const DrawOutline = ({ className, rectClassName, rx = 7.5, strokeWidth = 1 }) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const gradientId = `draw-outline-gradient-${uid}`
  const inset = strokeWidth / 2
  const svgRef = useRef(null)
  const [size, setSize] = useState(null)

  useEffect(() => {
    const svg = svgRef.current
    if (!svg || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize(prev => (prev && prev.w === width && prev.h === height ? prev : { w: width, h: height }))
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [])

  return (
    <svg ref={svgRef} className={className} aria-hidden="true" focusable="false">
      <defs>
        {/* SVG gradients can't reference a CSS custom property, so these stops
            mirror --color-brand-gradient (light theme) from _theme.scss —
            keep them in sync if that token's stops ever change. */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="3%" stopColor="#ff5129" />
          <stop offset="11%" stopColor="#f7333f" />
          <stop offset="20%" stopColor="#ef1755" />
          <stop offset="28%" stopColor="#ea0663" />
          <stop offset="35%" stopColor="#e90068" />
          <stop offset="44%" stopColor="#ce0179" />
          <stop offset="63%" stopColor="#8b06a4" />
          <stop offset="80%" stopColor="#200dea" />
          <stop offset="99%" stopColor="#0010ff" />
        </linearGradient>
      </defs>
      {size && size.w > strokeWidth && size.h > strokeWidth && (
        <rect
          className={rectClassName}
          x={inset}
          y={inset}
          width={size.w - strokeWidth}
          height={size.h - strokeWidth}
          rx={rx}
          ry={rx}
          pathLength="1"
          stroke={`url(#${gradientId})`}
        />
      )}
    </svg>
  )
}

export default DrawOutline
